import { useMemo } from 'react';
import { useProjectStore } from '../../../store/projectStore';
import { useTargets } from '../../../hooks/useTargets';
import { useAutoSelectCompany } from '../../../hooks/useAutoSelectCompany';
import type { WorkspaceSummary } from '../../../electron-shared/workspace/workspace-descriptor';
import {
  useAttackSurfaceGroups,
  type AttackSurfaceGroup,
} from '../../layout/target-selector/useAttackSurfaceGroups';
import {
  buildLauncherTargetItems,
  type LauncherSurfaceFilter,
  type LauncherTargetItem,
} from './launcher-targets';

export type LauncherTargetsStatus =
  | 'preparing'
  | 'notReady'
  | 'loading'
  | 'error'
  | 'ready';

export function useLauncherTargets({
  query,
  surfaceFilter,
  recents,
  tenantId,
}: {
  query: string;
  surfaceFilter: LauncherSurfaceFilter;
  recents: readonly WorkspaceSummary[];
  tenantId: string | null;
}): {
  status: LauncherTargetsStatus;
  surfaceGroups: AttackSurfaceGroup[];
  items: LauncherTargetItem[];
} {
  const companyId = useProjectStore((state) => state.companyId);
  const { targets, catalogSliceStatus, isLoading, isFetched, error } =
    useTargets({ companyId });
  const { isCompanyReady, isResolvingCompany } = useAutoSelectCompany();
  const surfaceGroups = useAttackSurfaceGroups({ targets, catalogSliceStatus });

  const items = useMemo(
    () =>
      buildLauncherTargetItems({
        groups: surfaceGroups,
        query,
        surfaceFilter,
        recents,
        tenantId,
      }),
    [query, recents, surfaceFilter, surfaceGroups, tenantId],
  );

  const isCatalogLoading =
    isLoading || (isCompanyReady && Boolean(companyId) && !isFetched && !error);
  const status: LauncherTargetsStatus = isResolvingCompany
    ? 'preparing'
    : !isCompanyReady
      ? 'notReady'
      : isCatalogLoading
        ? 'loading'
        : error
          ? 'error'
          : 'ready';

  return { status, surfaceGroups, items };
}
